import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Table, Button, Form, Row, Col, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { getMissingComprobantes, regenerateComprobantes } from '../../api/comprobantes.api.ts';
import '../../assets/sass/pages/admin-comprobantes.scss';

/**
 * Página de Administración - Regeneración de Comprobantes Faltantes
 * Permite a los administradores identificar y regenerar comprobantes de GetNet
 * que no se generaron automáticamente.
 */
const ComprobantesFaltantes = () => {
  const { t } = useTranslation();
  const [faltantes, setFaltantes] = useState([]);
  const [filteredFaltantes, setFilteredFaltantes] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [cargando, setCargando] = useState(true);
  const [regenerando, setRegenerando] = useState(false);
  const [toasts, setToasts] = useState([]);

  // Filtros
  const [filtros, setFiltros] = useState({
    startDate: '',
    endDate: '',
    usuario: '',
    gira: ''
  });

  // Cargar comprobantes faltantes al montar
  useEffect(() => {
    cargarFaltantes();
  }, []);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    aplicarFiltros();
  }, [faltantes, filtros]);

  /**
   * Cargar lista de comprobantes faltantes desde la API
   */
  const cargarFaltantes = async () => {
    try {
      setCargando(true);
      const response = await getMissingComprobantes();

      if (!response.error && response.data) {
        setFaltantes(response.data);
        setSeleccionados(new Set()); // Limpiar selecciones
      } else {
        addToast(
          'error',
          t('admin.comprobantes.error_cargar') || 'Error al cargar comprobantes faltantes',
          response.msg || 'Error desconocido'
        );
      }
    } catch (error) {
      console.error('Error cargando comprobantes faltantes:', error);
      addToast('error', 'Error al cargar datos', error.message);
    } finally {
      setCargando(false);
    }
  };

  /**
   * Aplicar filtros a la lista de faltantes
   */
  const aplicarFiltros = () => {
    let filtered = [...faltantes];

    if (filtros.usuario) {
      const userFilter = filtros.usuario.toLowerCase();
      filtered = filtered.filter(f => 
        f.usuario.toLowerCase().includes(userFilter) ||
        f.orden.toLowerCase().includes(userFilter)
      );
    }

    if (filtros.gira) {
      filtered = filtered.filter(f => 
        f.gira.toLowerCase().includes(filtros.gira.toLowerCase())
      );
    }

    if (filtros.startDate) {
      filtered = filtered.filter(f => f.fecha_pago >= filtros.startDate);
    }

    if (filtros.endDate) {
      filtered = filtered.filter(f => f.fecha_pago <= filtros.endDate);
    }

    setFilteredFaltantes(filtered);
  };

  /**
   * Toggle selección de un comprobante
   */
  const toggleSeleccion = (webpayId) => {
    const nuevaSeleccion = new Set(seleccionados);
    if (nuevaSeleccion.has(webpayId)) {
      nuevaSeleccion.delete(webpayId);
    } else {
      nuevaSeleccion.add(webpayId);
    }
    setSeleccionados(nuevaSeleccion);
  };

  /**
   * Seleccionar todos los comprobantes mostrados
   */
  const seleccionarTodos = () => {
    if (seleccionados.size === filteredFaltantes.length && filteredFaltantes.length > 0) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(filteredFaltantes.map(f => f.webpay_id)));
    }
  };

  /**
   * Regenerar comprobantes seleccionados
   */
  const regenerarSeleccionados = async () => {
    if (seleccionados.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('admin.comprobantes.seleccione_minimo') || 'Seleccione al menos un comprobante',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    // Confirmación
    const result = await Swal.fire({
      icon: 'warning',
      title: t('admin.comprobantes.confirmar_regeneracion') || '¿Regenerar comprobantes?',
      text: `${t('admin.comprobantes.regenerara') || 'Se regenerarán'} ${seleccionados.size} ${t('admin.comprobantes.comprobantes') || 'comprobantes'}`,
      showCancelButton: true,
      confirmButtonText: t('common.confirmar') || 'Confirmar',
      cancelButtonText: t('common.cancelar') || 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      setRegenerando(true);
      const webpayIds = Array.from(seleccionados);
      
      const response = await regenerateComprobantes(webpayIds);

      if (!response.error) {
        // Mostrar resultados
        const { total_regenerados, total_errores, resultados } = response;
        
        addToast(
          'success',
          t('admin.comprobantes.regeneracion_completada') || 'Regeneración completada',
          `${total_regenerados} exitosos, ${total_errores} errores`
        );

        // Mostrar detalles de errores si existen
        if (total_errores > 0) {
          const errores = resultados.filter(r => !r.exito);
          console.error('Errores en regeneración:', errores);
        }

        // Recargar lista
        await cargarFaltantes();
      } else {
        addToast('error', 'Error en regeneración', response.msg);
      }
    } catch (error) {
      console.error('Error regenerando comprobantes:', error);
      addToast('error', 'Error durante regeneración', error.message);
    } finally {
      setRegenerando(false);
    }
  };

  /**
   * Agregar toast a la lista
   */
  const addToast = (tipo, titulo, mensaje) => {
    const id = Date.now();
    setToasts([...toasts, { id, tipo, titulo, mensaje }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  return (
    <Container fluid className="admin-comprobantes-page py-4">
      <div className="page-header mb-4">
        <h1>{t('admin.comprobantes.titulo') || 'Regeneración de Comprobantes'}</h1>
        <p className="text-muted">
          {t('admin.comprobantes.descripcion') || 'Identifique y regenere comprobantes de GetNet que no se generaron automáticamente'}
        </p>
      </div>

      {/* FILTROS */}
      <div className="filters-card card mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">{t('common.filtros') || 'Filtros'}</h5>
          <Row>
            <Col md={6} lg={3} className="mb-3">
              <Form.Group>
                <Form.Label>{t('admin.comprobantes.fecha_inicio') || 'Fecha inicio'}</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.startDate}
                  onChange={(e) => setFiltros({ ...filtros, startDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Form.Group>
                <Form.Label>{t('admin.comprobantes.fecha_fin') || 'Fecha fin'}</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.endDate}
                  onChange={(e) => setFiltros({ ...filtros, endDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Form.Group>
                <Form.Label>{t('admin.comprobantes.usuario') || 'Usuario'}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('admin.comprobantes.nombre_rut') || 'Nombre o RUT'}
                  value={filtros.usuario}
                  onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Form.Group>
                <Form.Label>{t('admin.comprobantes.gira') || 'Gira'}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('admin.comprobantes.nombre_gira') || 'Nombre de gira'}
                  value={filtros.gira}
                  onChange={(e) => setFiltros({ ...filtros, gira: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      {/* ESTADO DE CARGA */}
      {cargando ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.cargando') || 'Cargando...'}</span>
          </Spinner>
          <p className="mt-3">{t('admin.comprobantes.cargando_faltantes') || 'Cargando comprobantes faltantes...'}</p>
        </div>
      ) : filteredFaltantes.length === 0 ? (
        <div className="alert alert-info text-center py-5">
          <h5>{t('admin.comprobantes.no_faltantes') || 'No hay comprobantes faltantes'}</h5>
          <p className="text-muted mb-0">
            {faltantes.length === 0
              ? t('admin.comprobantes.todas_regeneradas') || 'Todos los comprobantes han sido regenerados'
              : t('admin.comprobantes.filtros_no_resultados') || 'Los filtros actuales no devuelven resultados'}
          </p>
        </div>
      ) : (
        <>
          {/* BARRA DE ACCIONES */}
          <div className="actions-bar card mb-4">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted">
                  {t('admin.comprobantes.seleccionados') || 'Seleccionados'}: <strong>{seleccionados.size}</strong> / {filteredFaltantes.length}
                </span>
              </div>
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="me-2"
                  onClick={seleccionarTodos}
                >
                  {seleccionados.size === filteredFaltantes.length && filteredFaltantes.length > 0
                    ? t('common.deseleccionar_todo') || 'Deseleccionar todo'
                    : t('common.seleccionar_todo') || 'Seleccionar todo'}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={regenerarSeleccionados}
                  disabled={seleccionados.size === 0 || regenerando}
                >
                  {regenerando ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('admin.comprobantes.regenerando') || 'Regenerando...'}
                    </>
                  ) : (
                    t('admin.comprobantes.regenerar_seleccionados') || 'Regenerar seleccionados'
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* TABLA DE COMPROBANTES */}
          <div className="table-responsive card">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th width="50">
                    <Form.Check
                      checked={seleccionados.size === filteredFaltantes.length && filteredFaltantes.length > 0}
                      onChange={seleccionarTodos}
                      disabled={regenerando}
                    />
                  </th>
                  <th>{t('admin.comprobantes.orden') || 'Orden'}</th>
                  <th>{t('admin.comprobantes.monto') || 'Monto'}</th>
                  <th>{t('admin.comprobantes.fecha_pago') || 'Fecha pago'}</th>
                  <th>{t('admin.comprobantes.usuario') || 'Usuario'}</th>
                  <th>{t('admin.comprobantes.gira') || 'Gira'}</th>
                  <th>{t('admin.comprobantes.razon') || 'Razón falta'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFaltantes.map((faltante) => (
                  <tr key={faltante.webpay_id} className={seleccionados.has(faltante.webpay_id) ? 'table-active' : ''}>
                    <td>
                      <Form.Check
                        checked={seleccionados.has(faltante.webpay_id)}
                        onChange={() => toggleSeleccion(faltante.webpay_id)}
                        disabled={regenerando}
                      />
                    </td>
                    <td>
                      <code>{faltante.orden}</code>
                    </td>
                    <td className="text-end">
                      <strong>${faltante.monto.toLocaleString('es-CL')}</strong>
                    </td>
                    <td>
                      <small>{faltante.fecha_pago}</small>
                    </td>
                    <td>
                      <small>{faltante.usuario}</small>
                    </td>
                    <td>
                      <small>{faltante.gira}</small>
                    </td>
                    <td>
                      <span className="badge bg-warning text-dark">
                        {faltante.razon_falta}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* INFO ADICIONAL */}
          <div className="mt-3 text-muted small">
            <p>
              {t('admin.comprobantes.mostrando') || 'Mostrando'} {filteredFaltantes.length} {t('admin.comprobantes.de') || 'de'} {faltantes.length} {t('admin.comprobantes.comprobantes_faltantes') || 'comprobantes faltantes'}
            </p>
          </div>
        </>
      )}

      {/* TOASTS */}
      <ToastContainer position="top-end" className="p-3">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            bg={toast.tipo}
            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
            show={true}
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">{toast.titulo}</strong>
            </Toast.Header>
            <Toast.Body>{toast.mensaje}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </Container>
  );
};

export default ComprobantesFaltantes;
